import React, { Component } from 'react';
import { Classes, Position } from '@blueprintjs/core';
import classNames from 'classnames';

import Box from 'core/components/Box';
import Icon from 'core/components/Icon';
import Spinner from 'core/components/Spinner';
import Flex from 'core/components/Flex';
import Popover from 'core/components/Popover';
import Button from 'core/components/Button';
import Tooltip from 'core/components/Tooltip';
import MenuDivider from 'core/components/MenuDivider';
import { getOptionsWithNone } from 'core/form/components/modalSelect/selectHelpers';
import { DOWN, ENTER, ESC, SPACE, TAB, UP } from 'core/util/keyCodes';

import SelectPopoverInput from './SelectPopoverInput';

export const defaultOptionRenderer = (props) => {
  const {
    className,
    disabled,
    disabledTooltip,
    focused,
    handleOptionRef,
    iconCls,
    iconName,
    icon,
    key,
    label,
    selectItem,
    selected,
    style,
    separator = false,
    value,
    optionFormatter,
    groupHeaderProps,
    multi,
    toggle
  } = props;

  if (separator) {
    return (
      <div
        key={`separator-${key || value}`}
        ref={focused && handleOptionRef}
        style={style}
        tabIndex={focused && handleOptionRef ? '0' : undefined}
        aria-label="not selectable"
      >
        <MenuDivider />
      </div>
    );
  }

  if (groupHeaderProps) {
    return (
      <Box
        key={key || value}
        className={classNames(className, Classes.MENU_HEADER, { 'focusable-option': focused && handleOptionRef })}
        bg="selectGroupHeader.grayBackground"
        border="none"
        m="0 -4px"
        position="sticky"
        role="option"
        cursor="auto"
        tabIndex={focused && handleOptionRef ? '0' : undefined}
        ref={focused && handleOptionRef}
        {...groupHeaderProps}
      >
        {label}
      </Box>
    );
  }

  const onClick = (!selected || (multi && toggle)) && !disabled ? () => selectItem(value) : undefined;
  const iconType = iconCls || iconName || icon;
  const ariaProps = {};
  if (selected) {
    ariaProps['aria-selected'] = selected;
  }

  /*
  re autofocus

  If your input element is dynamically added to the DOM after the initial render, autoFocus might not work reliably.
  */

  const content = (
    <div
      key={key || value}
      className={className}
      onClick={onClick}
      style={style}
      tabIndex={focused && handleOptionRef ? '0' : undefined}
      ref={focused && handleOptionRef}
    >
      {iconType && (
        <Box>
          <Icon icon={iconType} mr={1} />
        </Box>
      )}
      <Box
        role="option"
        flex={1}
        {...ariaProps}
        tabIndex={focused && handleOptionRef ? '0' : undefined}
        ref={focused && handleOptionRef}
      >
        {optionFormatter ? optionFormatter(props) : label}
      </Box>
    </div>
  );

  if (disabledTooltip && disabled) {
    return (
      <Tooltip key={key || value} content={disabledTooltip} wrapperTagName="div">
        {content}
      </Tooltip>
    );
  }

  return content;
};

class SelectPopover extends Component {
  static defaultProps = {
    disabled: false,
    disabledValues: [],
    loading: false,
    menuHeight: 300,
    menuWidth: 230,
    multi: false,
    optionLimit: -1,
    ellipsisOptions: true,
    optionRenderer: defaultOptionRenderer,
    popoverPosition: Position.BOTTOM_LEFT,
    popoverMinimal: true,
    modifiers: {
      keepTogether: { enabled: true },
      flip: { enabled: true, boundariesElement: 'viewport' },
      preventOverflow: { enabled: true, boundariesElement: 'viewport' },
      offset: { enabled: true, offset: '0,2px' }
    },
    rowHeight: 28,
    showFilter: false,
    style: {},
    focusTopOptionDefault: false,
    parentKeyHandler: null,
    parentSetIndex: null,
    blockStandardFocus: false,
    dataTestId: 'selectPopover',
    disableFilterTools: false
  };

  state = {
    optionsFilter: ''
  };

  constructor(props) {
    super(props);
    this.handleOptionRef = this.handleOptionRef.bind(this);
    this.popupDidOpen = this.popupDidOpen.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { focusTopOptionDefault, isOpen, showFilter } = this.props;

    if (!prevProps.isOpen && isOpen && (showFilter || focusTopOptionDefault)) {
      this.filterOptions('');
      this.popupDidOpen();
    } else if (!prevProps.focusTopOptionDefault && focusTopOptionDefault && isOpen) {
      this.popupDidOpen();
    }
  }

  handleFilterOptions = (filter) => {
    const { onFilterOptionsChange } = this.props;
    this.filterOptions(filter);

    if (onFilterOptionsChange) {
      onFilterOptionsChange(filter);
    }
  };

  handleKeyboardEvents = (e, keyOrigin = null) => {
    const { parentKeyHandler, showFilter } = this.props;

    /*
      If the parent handler is passed in, we use it instead AND assume that props will dictate the selectedIndex
    */
    if (parentKeyHandler && !showFilter) {
      parentKeyHandler(e, keyOrigin);
      return;
    }

    const { onClose, onSelectItem, values, preventEnterSelection } = this.props;
    const { selectedIndex } = this.state;
    const selectOptions = this.getFilteredOptions();

    const key = e.keyCode ? e.keyCode : e.which;
    const keyCodes = [UP, DOWN, ENTER, ESC, SPACE];
    if (keyOrigin === 'filterInput') {
      keyCodes.push(TAB);
    }

    if (keyCodes.includes(key)) {
      e.stopPropagation();

      if (key === DOWN) {
        this.setState(
          {
            selectedIndex: selectedIndex < selectOptions.length - 1 ? selectedIndex + 1 : 0
          },
          this.updateScrollPosition
        );
      }
      if (key === UP) {
        this.setState(
          {
            selectedIndex: selectedIndex > 0 ? selectedIndex - 1 : selectOptions.length - 1
          },
          this.updateScrollPosition
        );
      }

      if (keyOrigin === 'filterInput') {
        // have to let SPACE be space and not select
        if (key === ENTER && !preventEnterSelection) {
          const option = selectOptions[selectedIndex === -1 ? 0 : selectedIndex];
          if (option && values !== option.value) {
            onSelectItem(option.value, option);
          } else {
            onClose();
          }

          this.setState({ optionsFilter: '', selectedIndex: -1, options: [] });
        }

        if (key === ESC) {
          onClose();
          this.setState({ optionsFilter: '', selectedIndex: -1, options: [] });
        }
      } else {
        if ((key === SPACE || key === ENTER) && !preventEnterSelection) {
          const option = selectOptions[selectedIndex === -1 ? 0 : selectedIndex];
          if (option && values !== option.value) {
            onSelectItem(option.value, option, true);
          } else {
            onClose();
          }
          this.setState({ optionsFilter: '', selectedIndex: -1, options: [] });
        }

        if (key === ESC) {
          onClose();
          this.setState({ optionsFilter: '', selectedIndex: -1, options: [] });
        }
      }
    }
  };

  handleSelectItem = (value) => {
    const { onSelectItem, keepOpen } = this.props;
    const { options } = this.state;

    if (!keepOpen) {
      this.setState({ optionsFilter: '', selectedIndex: -1, options: [] });
    }

    let option;
    if (options) {
      option = options.find((opt) => opt.value === value);
    }

    onSelectItem(value, option);
  };

  updateScrollPosition = () => {
    const { selectedIndex } = this.state;
    const { isOpen } = this.props; // showFilter

    // SHOULD it be different if it's a filter?

    if (this.scrollEl) {
      const itemHeight = this.scrollEl.firstChild.getBoundingClientRect().height;
      const selectedIndexY = (selectedIndex + 1) * itemHeight;

      if (selectedIndexY > this.scrollEl.scrollTop + this.scrollEl.offsetHeight) {
        this.scrollEl.scrollTop = selectedIndexY - this.scrollEl.offsetHeight + itemHeight;
      } else if (selectedIndexY - itemHeight < this.scrollEl.scrollTop) {
        let filterHeight = 0;
        if (this.inputEl) {
          filterHeight = this.inputEl?.closest('.select-popover-input')?.clientHeight;
        }
        this.scrollEl.scrollTop = selectedIndexY - (itemHeight + filterHeight);
      }

      if (this.focusedOption && isOpen) {
        const y = this.scrollEl.scrollTop;
        const x = this.scrollEl.scrollLeft;
        // Let other things settle before we focus!
        setTimeout(() => {
          this.focusedOption?.focus({ preventScroll: false });
        }, 0);
        this.scrollEl.scrollTo(x, y);
      }
    }
  };

  handleScrollRef = (refs) => {
    const { scrollRef } = this.props;

    if (scrollRef) {
      scrollRef(refs);
    } else {
      this.scrollEl = refs;
    }
  };

  focusInput = () => {
    if (this.inputEl) {
      this.inputEl.focus();
    }
  };

  popupDidOpen = () => {
    const { focusTopOptionDefault, isOpen } = this.props;
    if (!isOpen) {
      return;
    }
    if (this.inputEl) {
      this.inputEl.focus();
    } else if (this.focusedOption && focusTopOptionDefault) {
      const { showFilter, parentSetIndex } = this.props;

      const { selectedIndex } = showFilter ? this.state : this.props;
      if (selectedIndex === -1 || selectedIndex === undefined) {
        if (showFilter) {
          this.setState({ selectedIndex: 0 }, this.updateScrollPosition);
        } else if (parentSetIndex) {
          parentSetIndex(0);
        }
      }
      if (typeof this.focusedOption?.focus !== 'function') {
        return;
      }
      this.focusedOption?.focus();
    }
  };

  handleOptionRef = (ref) => {
    const { popoverRef } = this.props;
    if (ref) {
      this.focusedOption = ref;
      if (popoverRef) {
        popoverRef(ref, true);
      }
    }
  };

  filterOptions(optionsFilter) {
    const { clearable, onQuery, values } = this.props;

    if (onQuery) {
      this.setState({ optionsFilter });

      const { selectedIndex, options } = this.state;

      const value =
        selectedIndex >= 0 && options && options.length > selectedIndex ? options[selectedIndex].value : values;

      onQuery(optionsFilter).then((newOptions) => {
        let newSelectedIndex = newOptions.findIndex((option) => option.value === value);
        if (clearable) {
          newSelectedIndex += 1;
        }
        this.setState({ options: newOptions, selectedIndex: newSelectedIndex });
        this.focusInput();
      });
    } else {
      this.setState({ optionsFilter, selectedIndex: -1 });
    }
  }

  getFilteredOptions() {
    const { clearable, clearableLabel, minFilterChars, onQuery, options, optionLimit, showFilter } = this.props;
    const { optionsFilter } = showFilter ? this.state : this.props;

    let filteredOptions = options;

    if (onQuery) {
      const { options: stateOptions } = this.state;
      filteredOptions = stateOptions || [];
    } else if (optionsFilter) {
      const filter = optionsFilter.toLowerCase();
      filteredOptions = options.filter(
        ({ label, value, filterLabel }) =>
          (filterLabel && filterLabel.toLowerCase().includes(filter)) ||
          (typeof label === 'string' && label.toLowerCase().includes(filter)) ||
          (typeof label === 'number' && label.toString().startsWith(filter)) ||
          (typeof value === 'string' && value.toLowerCase().includes(filter)) ||
          (typeof value === 'number' && value.toString().startsWith(filter))
      );
    }

    if (minFilterChars > 0 && optionsFilter.length < minFilterChars) {
      return [];
    }

    const allFilteredOptions = clearable ? getOptionsWithNone(filteredOptions, clearableLabel) : filteredOptions;

    if (optionLimit > 0 && showFilter) {
      return allFilteredOptions.slice(0, optionLimit);
    }

    return allFilteredOptions;
  }

  renderFilterInput(filteredOptions) {
    const {
      showFilter,
      filterPlaceholder = 'Filter options...',
      small,
      multi,
      fetchingOptions,
      onSelectItem,
      onSelectAll,
      showClearButton,
      showSelectAll,
      disableFilterTools
    } = this.props;
    const { optionsFilter } = this.state;

    if (!showFilter) {
      return null;
    }

    let rightElement;
    if (
      !disableFilterTools &&
      multi &&
      (optionsFilter || showSelectAll) &&
      !fetchingOptions &&
      filteredOptions &&
      filteredOptions.length > 0
    ) {
      rightElement = (
        <>
          <Button
            ml={showClearButton ? -6 : undefined}
            minimal
            intent="primary"
            small={small}
            textColor="primary"
            minHeight={small ? 20 : 24}
            onClick={() =>
              onSelectAll(
                filteredOptions.filter((o) => !o.isGroupHeader && !o.disabled && o.value !== '').map((o) => o.value), // only select all enabled items
                filteredOptions
              )
            }
          >
            Select All
          </Button>
          {showClearButton && (
            <Button
              ml={-6}
              minimal
              intent="primary"
              small={small}
              textColor="primary"
              minHeight={small ? 20 : 24}
              onClick={() => onSelectItem('')}
            >
              Clear
            </Button>
          )}
        </>
      );
    }

    if (disableFilterTools) {
      // when we elect to skip the 'Select All' and 'Clear' buttons in the filter input, render a button to clear the filter
      rightElement = (
        <Button
          icon="small-cross"
          intent="primary"
          small={small}
          textColor="primary"
          minHeight={small ? 20 : 24}
          disabled={optionsFilter.length === 0}
          onClick={() => this.handleFilterOptions('')}
          minimal
        />
      );
    }

    return (
      <SelectPopoverInput
        placeholder={filterPlaceholder}
        inputRef={(inputEl) => {
          this.inputEl = inputEl;
        }}
        onKeyDownCapture={(e) => {
          this.handleKeyboardEvents(e, 'filterInput');
        }}
        onChange={(e) => this.handleFilterOptions(e.target.value)}
        value={optionsFilter}
        small={small}
        rightElement={rightElement}
      />
    );
  }

  renderOption(option, index, options) {
    const {
      optionRenderer,
      clearable,
      multi,
      showFilter,
      values,
      disabledValues,
      optionFormatter,
      ellipsisOptions,
      focusTopOptionDefault,
      small,
      toggle
    } = this.props;

    const { selectedIndex } = showFilter ? this.state : this.props;
    let isFocused = false;

    if (selectedIndex === -1 && focusTopOptionDefault && index === 0) {
      isFocused = true;
    }

    // This is purely done to make sure it's a string compare (coerces) in multi select scenarios
    // It's not perfect because it'll match across delimiters
    const value = values && values.join ? values.join(',') : values;
    const disabled = option.disabled || disabledValues.includes(option.value);

    /* eslint eqeqeq: 0 */
    const selected =
      option.selected ||
      (multi ? values?.indexOf(option.value) >= 0 : value == option.value) ||
      (multi && clearable && option.value === '' && values.length === 0);

    const menuItemClassName = classNames(Classes.MENU_ITEM, option.className, {
      [Classes.TEXT_OVERFLOW_ELLIPSIS]: ellipsisOptions,
      [Classes.ACTIVE]: selected,
      [Classes.DISABLED]: disabled,
      [Classes.SMALL]: small
    });

    if (selectedIndex === index) {
      isFocused = true;
    }
    // Do not change the option object (causes issues) but we need to pass a legit key to the optionRenderer, so if it's missing:
    const key = option.key || option.value || `option-${index}`;

    return optionRenderer({
      ...option,
      className: menuItemClassName,
      selectItem: (!selected || (multi && toggle)) && !disabled ? this.handleSelectItem : () => {},
      focused: isFocused,
      handleOptionRef: this.handleOptionRef,
      selected,
      disabled,
      optionFormatter,
      multi,
      toggle,
      options,
      option,
      key
    });
  }

  renderOptions() {
    const { menuHeight, menuWidth, popoverRef, showFilter, fetchingOptions, small, dataTestId } = this.props;

    const padding = 4;
    const filteredOptions = this.getFilteredOptions();
    const optionsHeight = menuHeight - (showFilter ? 38 : 0);

    return (
      <div
        ref={(ref) => popoverRef(ref, false)}
        style={{ maxHeight: menuHeight, width: menuWidth || 180, overflow: 'hidden', borderRadius: 3 }}
        className="select-popover-options"
      >
        {this.renderFilterInput(filteredOptions)}
        <div
          ref={this.handleScrollRef}
          style={{ maxHeight: optionsHeight, overflow: 'auto', padding }}
          data-testid={`selectPopover-${dataTestId}`}
          onKeyDownCapture={this.handleKeyboardEvents}
        >
          {fetchingOptions && (
            <Flex alignItems="flex-start" p={1}>
              <Spinner size={18} />
            </Flex>
          )}
          {!fetchingOptions && filteredOptions.length === 0 && (
            <div
              className={classNames(Classes.MENU_ITEM, Classes.DISABLED, {
                [Classes.SMALL]: small
              })}
            >
              No options found
            </div>
          )}
          {!fetchingOptions &&
            filteredOptions.map((option, index) => this.renderOption(option, index, filteredOptions))}
        </div>
      </div>
    );
  }

  render() {
    const { boundary, children, isOpen, popoverPosition, popoverMinimal, showFilter, modifiers } = this.props;

    return (
      <div>
        <Popover
          autoFocus={showFilter}
          canEscapeKeyClose
          shouldReturnFocusOnClose={false} // We are not controlling the focus here but in Select now!
          content={this.renderOptions()}
          enforceFocus // This is important so that shouldReturnFocusOnClose will be in play as someone tabs out AND closes the popup
          isOpen={isOpen}
          position={popoverPosition || Position.BOTTOM_LEFT}
          modifiers={modifiers}
          onOpened={this.popupDidOpen}
          boundary={boundary}
          minimal={popoverMinimal}
          data-testid="popover"
        >
          {children}
        </Popover>
      </div>
    );
  }
}

export default SelectPopover;
